import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { createPortal } from 'react-dom';
import { currentDomain, currentPath } from '../Index';
import { usePromotionsData } from '../data/PromotionData';
import { useNavigate } from 'react-router-dom';
import useAppPromotionTheme from '../hooks/useAppPromotionTheme';
import JiliRushGame from '../snippets/games/JiliRushGame';
import CandyLoadingPage from '../snippets/games/CandyLoadingPage';
import PromotionSkeletonDetails from '../data/PromotionSkeletonDetails';

export default function JiliFortuneRush({ isLightMode }) {
    const isAppPromo = useAppPromotionTheme(isLightMode);
    const { promotionsData, isPromotionDataReady } = usePromotionsData();
    const data = useMemo(
        () =>
            promotionsData.find((promo) => promo.title === 'Jili Fortune Rush'),
        [promotionsData]
    );
    const [isMobile, setIsMobile] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showGame, setShowGame] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 841);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const loggedInState = localStorage.getItem('isLoggedIn') === 'true';
        setIsLoggedIn(loggedInState);
    }, []);

    // Only redirect when promotion data is ready
    // and the promotion genuinely does not exist.
    useEffect(() => {
        if (!isPromotionDataReady) return;

        if (!data) {
            navigate('/not-found', { replace: true });
        }
    }, [isPromotionDataReady, data, navigate]);

    // Show skeleton while waiting for server time.
    if (!isPromotionDataReady) {
        return <PromotionSkeletonDetails />;
    }

    // Avoid displaying incomplete promotion details
    // while redirecting to the not-found page.
    if (!data) {
        return null;
    }

    const handleShowGame = () => {
        setLoading(true);
        setShowGame(true);

        setTimeout(() => {
            setLoading(false);
        }, 3000);
    };

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Jili Fortune Rush</title>
                <link rel="icon" href="/images/fav.webp" sizes="32x32" />
                <meta name="description" content="ddd" />
                <link rel="canonical" href={`${currentDomain}${currentPath}`} />
            </Helmet>
            <div className="promotion-page-main all-page-main-container-min-height">
                <div className="promotion-page-container main-container-global-adjusted-padding">
                    <div className="promotion-tnc-page">
                        <div className="promotion-page-breadcrumb">
                            <a
                                href={
                                    isAppPromo
                                        ? '/promotion/'.replace(
                                              '/promotion',
                                              '/app-promotion'
                                          )
                                        : '/promotion/'
                                }
                            >
                                Promotions
                            </a>
                            / <span>{data.title}</span>
                        </div>
                        <div className="promotion-tnc-banner">
                            <img
                                src={isMobile ? data.imgSrc : data.bannerSrc}
                                alt={`FGH${data.title}`}
                            />
                            <div className="promotion-tnc-content">
                                <div className="promotion-tnc-info">
                                    <div
                                        className={`promotion-indication-message ${data.promotionMessage.class}`}
                                    >
                                        {data.promotionMessage.text}
                                    </div>
                                    <h1>Jili Fortune Rush</h1>
                                    <h2>
                                        Every Spin Counts, Every Milestone Pays!
                                    </h2>
                                    <div className="promotion-tnc-period">
                                        <strong> Event Period: </strong>
                                        <span>
                                            {' '}
                                            Oct 08 (12NN) - Nov 05 (11:59AM),
                                            2026{' '}
                                        </span>
                                    </div>
                                </div>
                                <div className="promotion-tnc-mechanic-content">
                                    {isLoggedIn && !isAppPromo && (
                                        <div className="promotion-tnc-btn-wrapper">
                                            <button
                                                className="promotion-cta-btn"
                                                onClick={handleShowGame}
                                            >
                                                View Game
                                            </button>
                                        </div>
                                    )}
                                    <strong>Promo Mechanics</strong>
                                    <ol>
                                        <li>
                                            <p>
                                                <strong className="highlight">
                                                    Daily Missions:
                                                </strong>{' '}
                                                Complete daily targets to earn
                                                tickets for the Month-End Raffle
                                                Draw. To qualify for a daily
                                                tier, players must meet both the
                                                Daily Turnover Target AND the
                                                Cumulative Target Deposit
                                                requirement within the same
                                                cut-off day (12:00 PM – 11:30
                                                AM).
                                            </p>
                                            <div className="table-wrapper">
                                                <table
                                                    className="promotion-tnc-table"
                                                    style={{ minWidth: '0px' }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Tier</th>
                                                            <th>
                                                                Daily Turnover
                                                                Target
                                                            </th>
                                                            <th>
                                                                Target
                                                                Deposit{' '}
                                                            </th>
                                                            <th>
                                                                Raffle Entries
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr>
                                                            <td>1</td>
                                                            <td>
                                                                1,000 - 14,999
                                                            </td>
                                                            <td>200</td>
                                                            <td>1 Ticket</td>
                                                        </tr>

                                                        <tr>
                                                            <td>2</td>
                                                            <td>
                                                                15,000 - 49,999
                                                            </td>
                                                            <td>3,000</td>
                                                            <td>5 Tickets</td>
                                                        </tr>

                                                        <tr>
                                                            <td>3</td>
                                                            <td>
                                                                50,000 - 149,999
                                                            </td>
                                                            <td>10,000</td>
                                                            <td>15 Tickets</td>
                                                        </tr>

                                                        <tr>
                                                            <td>4</td>
                                                            <td>
                                                                150,000 -
                                                                499,999
                                                            </td>
                                                            <td>30,000</td>
                                                            <td>50 Tickets</td>
                                                        </tr>

                                                        <tr>
                                                            <td>5</td>
                                                            <td>
                                                                500,000 above
                                                            </td>
                                                            <td>100,000</td>
                                                            <td>100 Tickets</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p>
                                                ⚠️ Note: Players receive the
                                                ticket allocation for the
                                                highest single tier unlocked per
                                                day. All tickets accumulated
                                                throughout the 28 days carry
                                                over to the Month-End Draw.
                                            </p>
                                            <p>
                                                🎟️ Month-End Raffle Draw : At
                                                the end of the 28-day campaign,
                                                all accumulated raffle tickets
                                                will be entered into a final
                                                prize draw.
                                            </p>
                                            <div className="table-wrapper">
                                                <table
                                                    className="promotion-tnc-table"
                                                    style={{ minWidth: '0px' }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>
                                                                Raffle Prize
                                                            </th>
                                                            <th>Value</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                Spa & Dining
                                                                Experience
                                                                Voucher{' '}
                                                            </td>
                                                            <td>25,000.00</td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                Grocery
                                                                Voucher{' '}
                                                            </td>
                                                            <td>10,000.00</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Cash Prize </td>
                                                            <td>3,000.00</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p>
                                                📌Winner Announcement: Winners
                                                will be announced via the
                                                platform's official announcement
                                                page and notified through
                                                internal site inbox
                                                messages.{' '}
                                            </p>
                                        </li>

                                        <li>
                                            <strong className="highlight">
                                                Weeks 1 & 3 Free Spins
                                                Leaderboard:
                                            </strong>{' '}
                                            Compete for a top 30 spot on the
                                            weekly turnover leaderboard by
                                            playing JILI Slots From Thursday
                                            12nn to Thursday 11:30 AM!
                                            <ul style={{ marginBottom: '0px' }}>
                                                <li>
                                                    <strong className="highlight">
                                                        Minimum Qualifying
                                                        Turnover:
                                                    </strong>{' '}
                                                    5,000 minimum turnover to
                                                    qualify in the leaderboard
                                                </li>
                                                <li>
                                                    <strong className="highlight">
                                                        Crediting Day:
                                                    </strong>{' '}
                                                    Every Thursday at 3 PM
                                                </li>
                                                📌 Tie-Breaker: If two players
                                                end with the exact same
                                                turnover, the player who reached
                                                the turnover amount first takes
                                                the higher rank.
                                                <div className="table-wrapper">
                                                    <table
                                                        className="promotion-tnc-table"
                                                        style={{
                                                            minWidth: '0px',
                                                        }}
                                                    >
                                                        <thead>
                                                            <tr>
                                                                <th colSpan={3}>
                                                                    Free Spins
                                                                    Leaderboard{' '}
                                                                </th>
                                                            </tr>
                                                            <tr>
                                                                <th>
                                                                    Rank
                                                                    Position
                                                                </th>
                                                                <th>
                                                                    Reward with
                                                                    5 PHP bet
                                                                    value
                                                                </th>
                                                                <th>
                                                                    Rollover
                                                                    Requirement
                                                                </th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            <tr>
                                                                <td>R1</td>
                                                                <td>200</td>
                                                                <td rowSpan="4">
                                                                    Winnings has
                                                                    <br />
                                                                    NO Rollover
                                                                    <br />
                                                                    requirement
                                                                </td>
                                                            </tr>

                                                            <tr>
                                                                <td>R2 - R3</td>
                                                                <td>100</td>
                                                            </tr>

                                                            <tr>
                                                                <td>
                                                                    R4 - R10
                                                                </td>
                                                                <td>50</td>
                                                            </tr>

                                                            <tr>
                                                                <td>
                                                                    R11 - R30
                                                                </td>
                                                                <td>20</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                ⚠️Free Spin Expiration: Free
                                                spins credited on Thursdays must
                                                be used within seven (7) days of
                                                issuance. Unused free spins will
                                                automatically expire.
                                            </ul>
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                Weeks 2 & 4 Spin Fuel
                                            </strong>
                                            : Reach major daily turnover
                                            thresholds to collect your spin fuel
                                            after the daily cutoff.
                                            <div className="table-wrapper">
                                                <table
                                                    className="promotion-tnc-table"
                                                    style={{ minWidth: '0px' }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Milestone</th>
                                                            <th>
                                                                Turnover Target
                                                            </th>
                                                            <th>
                                                                Free Credits
                                                                Reward
                                                            </th>
                                                            <th>
                                                                Rollover
                                                                Requirement
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr>
                                                            <td>M1</td>
                                                            <td>
                                                                15,000 - 74,999
                                                            </td>
                                                            <td>50</td>
                                                            <td rowSpan="4">
                                                                1x Rollover
                                                                <br />
                                                                requirement
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>M2</td>
                                                            <td>
                                                                75,000 -<br />
                                                                349,999
                                                            </td>
                                                            <td>80</td>
                                                        </tr>

                                                        <tr>
                                                            <td>M3</td>
                                                            <td>
                                                                350,000 -<br />
                                                                1,499,999
                                                            </td>
                                                            <td>150</td>
                                                        </tr>
                                                        <tr>
                                                            <td>M4</td>
                                                            <td>1.5M</td>
                                                            <td>1,000</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p>
                                                ⚠️ Prize Expiration: Triggered
                                                Spin Fuel Rewards do not expire
                                                daily! Players can claim their
                                                earned rewards via the floating
                                                icon at any time during the
                                                promo period.{' '}
                                            </p>
                                            <p>
                                                ⚠️ As your daily turnover
                                                increases, each milestone you
                                                reach unlocks a claimable Free
                                                Credits reward.
                                            </p>
                                        </li>
                                    </ol>
                                    <strong>Terms and Conditions </strong>
                                    <ol>
                                        <li>
                                            <strong className="highlight">
                                                Eligibility
                                            </strong>
                                            : Open to all registered FGH
                                            players{' '}
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                Qualification & Game
                                                Restrictions:
                                            </strong>{' '}
                                            Only valid wagers placed on JILI
                                            Slot games count toward turnover
                                            accumulation. Turnover on
                                            non-qualifying games
                                            <strong className="highlight">
                                                (e.g., JILI Fishing, Table
                                                Games, or other game providers)
                                            </strong>{' '}
                                            will not count toward daily
                                            milestones or leaderboard ranks.
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                Reset Cut-off Time
                                            </strong>
                                            : Daily milestone trackers and daily
                                            mission targets reset every day at
                                            11:30 AM.
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                Rollover Requirement
                                            </strong>
                                            : Free Credits are subject to a
                                            strict 1x rollover requirement
                                            before a withdrawal can be
                                            processed. Winnings derived from the
                                            awarded free spins has no rollover
                                            requirement and can be withdrawn
                                            immediately.
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                One Account Policy
                                            </strong>
                                            : This promotion is limited to one
                                            (1) unique account per player. The
                                            use of multiple accounts is strictly
                                            prohibited and will lead to
                                            immediate disqualification.
                                        </li>
                                        <li>
                                            <strong className="highlight">
                                                Management Rights
                                            </strong>
                                            : FGH reserves the right to alter,
                                            amend, or terminate this promotion,
                                            or any aspect of it, at its sole
                                            discretion at any time. In all
                                            matters relating to this promotion,
                                            including eligibility, winner
                                            selection, and dispute resolutions,
                                            the decisions of the FGH management
                                            team are absolute, final, and not
                                            subject to appeal. Any form of abuse
                                            or unfair play detected will result
                                            in the immediate forfeiture of
                                            rewards and potential account
                                            suspension.
                                        </li>
                                        {isAppPromo && (
                                            <p
                                                style={{
                                                    color: 'red',
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                **This game is only available on
                                                website.
                                            </p>
                                        )}
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showGame &&
                createPortal(
                    isLoading ? (
                        <CandyLoadingPage
                            onLoadingComplete={() => setIsLoading(false)}
                        />
                    ) : (
                        <JiliRushGame onClose={() => setShowGame(false)} />
                    ),
                    document.body
                )}
        </>
    );
}
